import { API_BASE_URL } from './api';
import { authService } from './authService';

export const GroupJoinMode = {
  InviteOnly: 'InviteOnly',
  OpenLink: 'OpenLink',
} as const;
export type GroupJoinModeValue = typeof GroupJoinMode[keyof typeof GroupJoinMode];

export const GroupRole = {
  Member: 'Member',
  Admin: 'Admin',
  Owner: 'Owner',
} as const;
export type GroupRoleValue = typeof GroupRole[keyof typeof GroupRole];

export interface DiveGroup {
  groupId: string;
  name: string;
  description?: string | null;
  photoKey?: string | null;
  ownerUserId: string;
  joinMode: GroupJoinModeValue;
  shareCode: string;
  memberCount: number;
  /** Whether this group appears in group search results. Defaults to true. */
  searchable: boolean;
  /** Owner opt-in to show the group's name, description and size to signed-out visitors. Defaults to false. */
  showOnPublicPage?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GroupMembership {
  groupId: string;
  userId: string;
  role: GroupRoleValue;
  status: 'Pending' | 'Active';
  invitedByUserId: string;
  displayName: string;
  profilePhotoUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGroupRequest {
  name: string;
  description?: string;
  joinMode?: GroupJoinModeValue;
}

export interface UpdateGroupRequest {
  name?: string;
  description?: string;
  photoKey?: string;
  joinMode?: GroupJoinModeValue;
  searchable?: boolean;
  showOnPublicPage?: boolean;
}

const authJsonHeaders = () => ({ 'Content-Type': 'application/json', ...authService.getAuthHeaders() });

// Group photos live in the same public bucket as post/profile photos, just under whatever
// key prefix the upload endpoint that created them used — no new infra needed.
export const groupPhotoUrl = (photoKey: string): string =>
  `https://aidivecoach-post-photos.s3.us-east-2.amazonaws.com/${photoKey}`;

const throwWithBody = async (res: Response, fallback: string) => {
  const text = await res.text();
  throw new Error(text || fallback);
};

export const groupService = {
  createGroup: async (request: CreateGroupRequest): Promise<DiveGroup> => {
    const res = await fetch(`${API_BASE_URL}/groups`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) return throwWithBody(res, 'Failed to create group');
    return res.json();
  },

  getMyGroups: async (): Promise<DiveGroup[]> => {
    const res = await fetch(`${API_BASE_URL}/groups/mine`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load groups');
    return res.json();
  },

  searchGroups: async (query: string): Promise<DiveGroup[]> => {
    if (query.trim().length < 2) return [];
    const res = await fetch(`${API_BASE_URL}/groups/search?q=${encodeURIComponent(query.trim())}`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to search groups');
    return res.json();
  },

  getGroup: async (groupId: string): Promise<DiveGroup> => {
    const res = await fetch(`${API_BASE_URL}/groups/${groupId}`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load group');
    return res.json();
  },

  getMembers: async (groupId: string): Promise<GroupMembership[]> => {
    const res = await fetch(`${API_BASE_URL}/groups/${groupId}/members`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load members');
    return res.json();
  },

  getPendingRequests: async (groupId: string): Promise<GroupMembership[]> => {
    const res = await fetch(`${API_BASE_URL}/groups/${groupId}/requests`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load requests');
    return res.json();
  },

  updateGroup: async (groupId: string, request: UpdateGroupRequest): Promise<DiveGroup> => {
    const res = await fetch(`${API_BASE_URL}/groups/${groupId}`, {
      method: 'PATCH',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) return throwWithBody(res, 'Failed to update group');
    return res.json();
  },

  deleteGroup: async (groupId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/groups/${groupId}`, {
      method: 'DELETE',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to delete group');
  },

  inviteMember: async (groupId: string, userId: string): Promise<GroupMembership> => {
    const res = await fetch(`${API_BASE_URL}/groups/${groupId}/invite`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ userId }),
    });
    if (!res.ok) return throwWithBody(res, 'Failed to invite member');
    return res.json();
  },

  joinByCode: async (shareCode: string): Promise<DiveGroup> => {
    const res = await fetch(`${API_BASE_URL}/groups/join-by-code`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ shareCode }),
    });
    if (!res.ok) return throwWithBody(res, 'Failed to join group');
    return res.json();
  },

  requestJoin: async (groupId: string): Promise<GroupMembership> => {
    const res = await fetch(`${API_BASE_URL}/groups/${groupId}/request-join`, {
      method: 'POST',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) return throwWithBody(res, 'Failed to request to join');
    return res.json();
  },

  approveRequest: async (groupId: string, userId: string): Promise<GroupMembership> => {
    const res = await fetch(`${API_BASE_URL}/groups/${groupId}/requests/${userId}/approve`, {
      method: 'POST',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) return throwWithBody(res, 'Failed to approve request');
    return res.json();
  },

  declineOrCancelRequest: async (groupId: string, userId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/groups/${groupId}/requests/${userId}`, {
      method: 'DELETE',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to decline request');
  },

  updateMemberRole: async (groupId: string, userId: string, role: GroupRoleValue): Promise<GroupMembership> => {
    const res = await fetch(`${API_BASE_URL}/groups/${groupId}/members/${userId}/role`, {
      method: 'PATCH',
      headers: authJsonHeaders(),
      body: JSON.stringify({ role }),
    });
    if (!res.ok) return throwWithBody(res, 'Failed to update role');
    return res.json();
  },

  removeMember: async (groupId: string, userId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/groups/${groupId}/members/${userId}`, {
      method: 'DELETE',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to remove member');
  },
};
